import React, { Component, Fragment } from 'react';
import { Alert, Button, Checkbox, Empty, Modal, Select, message } from 'antd';
import ApplicationCard from '../../components/ApplicationCard';
import ApplicationDetails from '../../components/ApplicationDetails';
import ApplicationForm from '../../components/ApplicationDetails/form';
import { Query, withApollo } from 'react-apollo';
import {
  GET_APPLICATIONS,
  AUTHORIZE_APPLICATION,
  RECONCILE_VERIFIED_APPLICATIONS,
  REJECT_APPLICATION
} from '../../api/application';
import ModalBox from '../../components/ModalBox';
import Loading from '../../components/Loading';
import './index.css';

const { Option } = Select;

class ApplicationList extends Component {
  state = {
    applicationType: 'all',
    statusType: 'pending',
    identityType: 'all',
    currentApplicationId: '',
    currentApplication: {},
    currentOperation: '',
    selectedApplicationIds: [],
    batchApproving: false,
    batchProgress: 0,
    syncingVerified: false
  };

  _getSearchTerm = () => {
    const { applicationType, statusType } = this.state;
    return {
      ...(applicationType !== 'all' && { type: applicationType }),
      ...(statusType !== 'all' && { status: statusType })
    };
  };

  _refreshApplications = () =>
    this.props.client.query({
      query: GET_APPLICATIONS,
      variables: { searchTerm: this._getSearchTerm() },
      fetchPolicy: 'network-only'
    });

  _setApplicationSelected = (_id, selected) => {
    this.setState(({ selectedApplicationIds }) => ({
      selectedApplicationIds: selected
        ? [...selectedApplicationIds, _id]
        : selectedApplicationIds.filter(id => id !== _id)
    }));
  };

  _filterApplicationsByIdentity = applications => {
    const { identityType } = this.state;
    if (identityType === 'verified') {
      return applications.filter(
        application =>
          application.applicant && application.applicant.identity
      );
    }
    if (identityType === 'unverified-pending') {
      return applications.filter(
        application =>
          application.status === 'pending' &&
          !(application.applicant && application.applicant.identity)
      );
    }
    return applications;
  };

  _handleBatchAuthorize = applications => {
    const selectedIds = this.state.selectedApplicationIds;
    const pendingApplications = applications.filter(
      item => item.status === 'pending' && selectedIds.includes(item._id)
    );
    if (!pendingApplications.length) return;

    Modal.confirm({
      title: `Authorize ${pendingApplications.length} applications?`,
      content:
        'Each application will run the complete authorization and notification workflow. This action cannot be undone.',
      okText: 'Authorize all',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: () => this._authorizeApplicationsSequentially(pendingApplications)
    });
  };

  _handleReconcileVerified = applications => {
    const staleApplications = applications.filter(
      application =>
        application.type === 'identity' &&
        application.status === 'pending' &&
        application.applicant &&
        application.applicant.identity
    );
    if (!staleApplications.length) return;

    Modal.confirm({
      title: `Sync ${staleApplications.length} verified applications?`,
      content:
        'Only the application status will be changed to authorized. Identities and user notifications will not be changed.',
      okText: 'Sync status',
      cancelText: 'Cancel',
      onOk: () => {
        this.setState({ syncingVerified: true });
        return this.props.client
          .mutate({
            mutation: RECONCILE_VERIFIED_APPLICATIONS,
            variables: { _ids: staleApplications.map(item => item._id) }
          })
          .then(({ data }) => {
            const updated = data.reconcileVerifiedApplications || [];
            return this._refreshApplications().then(() => updated.length);
          })
          .then(count => {
            this.setState({ syncingVerified: false });
            message.success(`${count} application statuses synchronized!`);
          })
          .catch(error => {
            this.setState({ syncingVerified: false });
            message.error(error.message || 'Failed to synchronize statuses.');
          });
      }
    });
  };

  _authorizeApplicationsSequentially = applications => {
    const results = [];
    this.setState({ batchApproving: true, batchProgress: 0 });

    return applications
      .reduce(
        (chain, application, index) =>
          chain.then(() =>
            this.props.client
              .mutate({
                mutation: AUTHORIZE_APPLICATION,
                variables: { _id: application._id }
              })
              .then(() => results.push({ _id: application._id, success: true }))
              .catch(error =>
                results.push({
                  _id: application._id,
                  success: false,
                  error
                })
              )
              .then(() => this.setState({ batchProgress: index + 1 }))
          ),
        Promise.resolve()
      )
      .then(this._refreshApplications)
      .then(() => {
        const succeededIds = results
          .filter(result => result.success)
          .map(result => result._id);
        const failed = results.filter(result => !result.success);

        this.setState(({ selectedApplicationIds }) => ({
          selectedApplicationIds: selectedApplicationIds.filter(
            id => !succeededIds.includes(id)
          ),
          batchApproving: false,
          batchProgress: 0
        }));

        if (!failed.length) {
          message.success(`${succeededIds.length} applications authorized!`);
        } else {
          message.warning(
            `${succeededIds.length} authorized, ${failed.length} failed. Failed applications remain selected.`
          );
        }
      })
      .catch(error => {
        this.setState({ batchApproving: false, batchProgress: 0 });
        message.error(error.message || 'Failed to refresh applications.');
      });
  };

  _handleAuthorize = () => {
    const {
      currentApplication: { _id },
      applicationType,
      statusType
    } = this.state;
    this.props.client
      .mutate({
        mutation: AUTHORIZE_APPLICATION,
        variables: { _id },
        refetchQueries: () => [
          {
            query: GET_APPLICATIONS,
            variables: {
              searchTerm: {
                ...(applicationType !== 'all' && { type: applicationType }),
                ...(statusType !== 'all' && { status: statusType })
              }
            }
          }
        ]
      })
      .then(() => message.success('Application authorized!'))
      .catch(err => message.error(err.message || 'Error occurred!'));
  };

  _handleReject = rejectReason => {
    const {
      currentApplication: { _id },
      applicationType,
      statusType
    } = this.state;
    this.props.client
      .mutate({
        mutation: REJECT_APPLICATION,
        variables: { _id, rejectReason },
        refetchQueries: () => [
          {
            query: GET_APPLICATIONS,
            variables: {
              searchTerm: {
                ...(applicationType !== 'all' && { type: applicationType }),
                ...(statusType !== 'all' && { status: statusType })
              }
            }
          }
        ]
      })
      .then(() => message.success('Application rejected!'))
      .catch(err => message.error(err.message || 'Error occurred!'));
  };

  _handleConfirm = () => {
    const { form } = this.formRef.props;
    if (!form) return this._closeModal();
    form.validateFields((err, values) => {
      if (err) return;
      const { _id, reason } = values || {};
      if (_id) {
        //Authorizing application
        if (_id === this.state.currentApplicationId) {
          //Authentication
          this._handleAuthorize();
        } else message.error('ID is not correct!');
      } else if (reason) {
        //Rejecting application
        this._handleReject(reason);
      }
    });
    this._closeModal();
  };

  _handleOperation = type => {
    this.setState({
      currentOperation: type
    });
  };

  _closeModal = () => {
    this.setState({
      currentApplicationId: '',
      currentApplication: {},
      currentOperation: ''
    });
  };

  render() {
    const {
      applicationType,
      statusType,
      identityType,
      currentApplicationId,
      currentOperation,
      selectedApplicationIds,
      batchApproving,
      batchProgress,
      syncingVerified
    } = this.state;
    const operationInProgress = batchApproving || syncingVerified;

    return (
      <div className="application-page">
        <h2>申请管理</h2>
        <div className="application-list-filters">
          <Select
            disabled={operationInProgress}
            onChange={type =>
              this.setState({
                applicationType: type,
                selectedApplicationIds: []
              })
            }
            defaultValue={applicationType}
          >
            <Option value="all">All applications</Option>
            <Option value="carton">Carton</Option>
            <Option value="identity">Identification</Option>
          </Select>

          <Select
            disabled={operationInProgress}
            onChange={type =>
              this.setState({ statusType: type, selectedApplicationIds: [] })
            }
            value={statusType}
          >
            <Option value="all">Any status</Option>
            <Option value="authorized">Authorized</Option>
            <Option value="pending">Pending</Option>
            <Option value="rejected">Rejected</Option>
          </Select>

          <Select
            disabled={operationInProgress}
            onChange={type =>
              this.setState({
                identityType: type,
                ...(type === 'unverified-pending' && { statusType: 'pending' }),
                selectedApplicationIds: []
              })
            }
            value={identityType}
          >
            <Option value="all">Any verification</Option>
            <Option value="verified">Verified</Option>
            <Option value="unverified-pending">Unverified pending</Option>
          </Select>
        </div>
        <Query
          query={GET_APPLICATIONS}
          variables={{
            searchTerm: {
              ...this._getSearchTerm()
            }
          }}
        >
          {({ loading, error, data, refetch }) => {
            if (loading) return <Loading />;
            if (error) {
              return (
                <Alert
                  className="application-list-error"
                  type="error"
                  showIcon
                  message="Failed to load applications"
                  description={
                    <span>
                      The database connection may be temporarily unavailable.{' '}
                      <Button size="small" onClick={() => refetch()}>
                        Retry
                      </Button>
                    </span>
                  }
                />
              );
            }
            let { applications = [] } = data;
            applications = this._filterApplicationsByIdentity(applications);

            const pendingApplications = applications.filter(
              application => application.status === 'pending'
            );
            const staleVerifiedApplications = applications.filter(
              application =>
                application.type === 'identity' &&
                application.status === 'pending' &&
                application.applicant &&
                application.applicant.identity
            );
            const visiblePendingIds = pendingApplications.map(
              application => application._id
            );
            const selectedVisibleCount = visiblePendingIds.filter(id =>
              selectedApplicationIds.includes(id)
            ).length;
            const allVisibleSelected =
              visiblePendingIds.length > 0 &&
              selectedVisibleCount === visiblePendingIds.length;

            return applications.length ? (
              <Fragment>
                <div className="application-list-toolbar">
                  <div>
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={
                        selectedVisibleCount > 0 && !allVisibleSelected
                      }
                      disabled={!visiblePendingIds.length || operationInProgress}
                      onChange={event =>
                        this.setState(({ selectedApplicationIds }) => ({
                          selectedApplicationIds: event.target.checked
                            ? Array.from(
                                new Set([
                                  ...selectedApplicationIds,
                                  ...visiblePendingIds
                                ])
                              )
                            : selectedApplicationIds.filter(
                                id => !visiblePendingIds.includes(id)
                              )
                        }))
                      }
                    >
                      Select pending ({selectedVisibleCount}/
                      {visiblePendingIds.length})
                    </Checkbox>
                    <span className="application-list-count">
                      Showing {applications.length} applications
                    </span>
                  </div>
                  <div className="application-list-actions">
                    <Button
                      icon="sync"
                      loading={syncingVerified}
                      disabled={
                        !staleVerifiedApplications.length || batchApproving
                      }
                      onClick={() =>
                        this._handleReconcileVerified(applications)
                      }
                    >
                      Sync verified ({staleVerifiedApplications.length})
                    </Button>
                    <Button
                      type="primary"
                      icon="check"
                      loading={batchApproving}
                      disabled={!selectedVisibleCount || syncingVerified}
                      onClick={() => this._handleBatchAuthorize(applications)}
                    >
                      {batchApproving
                        ? `Authorizing ${batchProgress}/${selectedVisibleCount}`
                        : `Authorize selected (${selectedVisibleCount})`}
                    </Button>
                  </div>
                </div>
                <div className="application-list-container">
                  {applications.map(application => (
                    <ApplicationCard
                      key={application._id}
                      application={application}
                      selected={selectedApplicationIds.includes(
                        application._id
                      )}
                      selectionDisabled={operationInProgress}
                      onSelect={selected =>
                        this._setApplicationSelected(application._id, selected)
                      }
                      onClick={
                        operationInProgress
                          ? undefined
                          : () =>
                              this.setState({
                                currentApplicationId: application._id,
                                currentApplication: application
                              })
                      }
                    />
                  ))}
                </div>
              </Fragment>
            ) : (
              <Empty className="empty-hint" />
            );
          }}
        </Query>
        <ModalBox
          className="application-detail-modal"
          visible={!!currentApplicationId}
          title={`Viewing application (id: ${currentApplicationId})`}
          onConfirm={() => {
            if (currentOperation) this._handleConfirm();
          }}
          onCancel={this._closeModal}
        >
          <ApplicationDetails
            _id={currentApplicationId}
            _handleOperation={this._handleOperation}
          />
          <ApplicationForm
            application={currentApplicationId}
            operation={currentOperation}
            onSetRef={ref => {
              this.formRef = ref;
            }}
            onSubmit={this._handleConfirm}
          />
        </ModalBox>
      </div>
    );
  }
}

export default withApollo(ApplicationList);
